import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import {
  SidePanelStack,
  SidePanelStackContent,
  useSidePanelStack,
} from "@/components/ui/SidePanelStack";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import type { Club, UserProfile } from "@/api/clubsApi";
import {
  useFetchClubMembersQuery,
  useFetchUserProfilesByIdsQuery,
  useDeleteClubMutation,
} from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";
import { EditClubFormContent } from "./CreateClubForm";
import { SubscriptionsPaymentsView } from "./payments";
import { MeetupImportWizard } from "./MeetupImportWizard";
import { InvitationDashboard } from "./InvitationDashboard";
import { ClubDataExportView } from "./ClubDataExportView";

interface ManageClubPanelProps {
  club: Club;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ManageClubPanel - Side panel for club management (owners/cohosts only)
 * Matches mobile manage-club screen structure:
 * - Club details
 * - Roles & permissions
 * - Subscriptions & payments
 * - Delete club
 */
export function ManageClubPanel({
  club,
  open,
  onOpenChange,
}: ManageClubPanelProps) {
  const initialView = useMemo(
    () => ({
      id: "main",
      title: "Manage Club",
      content: <ManageMainView club={club} onClose={() => onOpenChange(false)} />,
    }),
    [club, onOpenChange]
  );

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelStack initialView={initialView}>
          {({ currentView, canGoBack, onBack }) => (
            <>
              <SidePanelHeader
                title={currentView.title}
                onBack={canGoBack ? onBack : undefined}
                onClose={!canGoBack ? () => onOpenChange(false) : undefined}
              />
              <SidePanelStackContent />
            </>
          )}
        </SidePanelStack>
      </SidePanelContent>
    </SidePanel>
  );
}

// Main management menu view
function ManageMainView({
  club,
  onClose,
}: {
  club: Club;
  onClose: () => void;
}) {
  const { push, pop } = useSidePanelStack();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteClub, { isLoading: isDeleting }] = useDeleteClubMutation();

  // Fetch members for the roles view
  const { data: members } = useFetchClubMembersQuery(
    { clubId: club.id!, includeUserProfiles: false },
    { skip: !club.id }
  );

  const handleClubDetails = () => {
    push({
      id: "edit-details",
      title: "Club Details",
      content: (
        <EditClubFormContent
          club={club}
          onSave={() => {
            // Return to main manage view after saving
            pop();
          }}
        />
      ),
    });
  };

  const handleRolesPermissions = () => {
    push({
      id: "roles",
      title: "Roles & Permissions",
      content: <RolesPermissionsView clubId={club.id!} members={members ?? []} />,
    });
  };

  const handleSubscriptionsPayments = () => {
    push({
      id: "subscriptions",
      title: "Subscriptions & Payments",
      content: <SubscriptionsPaymentsView clubId={club.id!} />,
    });
  };

  const handleImportMembers = () => {
    push({
      id: "import-members",
      title: "Import members",
      content: <MeetupImportWizard clubId={club.id!} onClose={onClose} />,
    });
  };

  const handleViewInvitations = () => {
    push({
      id: "invitations",
      title: "Invitations",
      content: <InvitationDashboard clubId={club.id!} />,
    });
  };

  const handleExportClubData = () => {
    push({
      id: "export",
      title: "Export Club Data",
      content: <ClubDataExportView club={club} scope="club" />,
    });
  };

  const handleDeleteClub = async () => {
    try {
      const result = await deleteClub(club.id!).unwrap();
      setShowDeleteConfirm(false);
      onClose();

      const summary = result.cleanupSummary;
      if (summary && summary.totalSubscriptions > 0) {
        showToast(
          `Club deleted. ${summary.successfulCancellations} subscription(s) canceled, ${summary.successfulRefunds} refund(s) issued.`,
          "success"
        );
      } else {
        showToast("Club deleted successfully.", "success");
      }

      navigate({ to: "/" });
    } catch (error) {
      setShowDeleteConfirm(false);
      // The RTK-unwrapped rejection is a plain object, not an Error: the CUSTOM_ERROR
      // payload carries the edge function message in `.error`, while real Error/RTK
      // SerializedError rejections carry it in `.message`.
      const err = error as { error?: unknown; message?: unknown };
      const message =
        typeof err?.error === "string"
          ? err.error
          : typeof err?.message === "string"
            ? err.message
            : undefined;
      showToast(message ?? "Failed to delete club. Please try again.", "error");
    }
  };

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-6 pb-4">
        {/* Management Options */}
        <div className="bg-surface rounded-2xl overflow-hidden">
          <MenuItem
            icon="pen-to-square"
            label="Club details"
            onClick={handleClubDetails}
          />
          <Divider />
          <MenuItem
            icon="user-group"
            label="Roles & permissions"
            onClick={handleRolesPermissions}
          />
          <Divider />
          <MenuItem
            icon="credit-card"
            label="Subscriptions & payments"
            onClick={handleSubscriptionsPayments}
          />
          <Divider />
          <MenuItem
            icon="file-import"
            label="Import members"
            onClick={handleImportMembers}
          />
          <Divider />
          <MenuItem
            icon="envelope-open-text"
            label="Invitations"
            onClick={handleViewInvitations}
          />
          <Divider />
          <MenuItem
            icon="arrow-down-to-bracket"
            label="Export club data"
            onClick={handleExportClubData}
          />
        </div>

        {/* Delete Club Button */}
        <Button
          variant="ghost"
          className="w-full text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting}
        >
          Delete this club
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {club.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the club and cancel all member subscriptions.
              Members will receive prorated refunds where possible. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClub}
              className="bg-red-500 hover:bg-red-600"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Club"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidePanelBody>
  );
}

// Menu item component
function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-4 hover:bg-surface-1 transition-colors text-left"
    >
      <div className="w-8 h-8 rounded-lg bg-surface-1 flex items-center justify-center">
        <Icon name={icon} size={16} className="text-text-primary" />
      </div>
      <span className="flex-1 text-body-1 text-text-primary">{label}</span>
      <Icon name="angle-right" size={16} className="text-text-secondary" />
    </button>
  );
}

// Divider component
function Divider() {
  return <div className="h-px bg-border-subtle ml-14" />;
}

// Roles & Permissions view
function RolesPermissionsView({
  clubId,
  members,
}: {
  clubId: string;
  members: Array<{ user_id: string; role?: string }>;
}) {
  const { push } = useSidePanelStack();

  // Get all member IDs
  const memberIds = useMemo(() => {
    return members.map((m) => m.user_id).filter(Boolean) as string[];
  }, [members]);

  // Fetch profiles
  const { data: profiles = [], isLoading } = useFetchUserProfilesByIdsQuery(
    memberIds,
    { skip: memberIds.length === 0 }
  );

  // Create profile map
  const profileMap = useMemo(() => {
    return new Map(profiles.map((p) => [p.id, p]));
  }, [profiles]);

  // Separate by role
  const { owners, cohosts, regularMembers } = useMemo(() => {
    const owners: Array<{ member: typeof members[0]; profile: UserProfile }> = [];
    const cohosts: Array<{ member: typeof members[0]; profile: UserProfile }> = [];
    const regular: Array<{ member: typeof members[0]; profile: UserProfile }> = [];

    members.forEach((member) => {
      const profile = profileMap.get(member.user_id);
      if (!profile) return;

      if (member.role === "owner") {
        owners.push({ member, profile });
      } else if (member.role === "cohost") {
        cohosts.push({ member, profile });
      } else {
        regular.push({ member, profile });
      }
    });

    return { owners, cohosts, regularMembers: regular };
  }, [members, profileMap]);

  const handleMemberClick = (userId: string) => {
    push({
      id: `member-${userId}`,
      title: "Member Details",
      content: (
        <MemberDetailView
          clubId={clubId}
          userId={userId}
          members={members}
        />
      ),
    });
  };

  if (isLoading) {
    return (
      <SidePanelBody className="pt-0">
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </SidePanelBody>
    );
  }

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-6 pb-4">
        {/* Owners */}
        {owners.length > 0 && (
          <MemberRoleSection
            title="Owner"
            members={owners}
            onMemberClick={handleMemberClick}
          />
        )}

        {/* Cohosts */}
        {cohosts.length > 0 && (
          <MemberRoleSection
            title="Co-hosts"
            members={cohosts}
            onMemberClick={handleMemberClick}
          />
        )}

        {/* Regular Members */}
        {regularMembers.length > 0 && (
          <MemberRoleSection
            title="Members"
            members={regularMembers}
            onMemberClick={handleMemberClick}
          />
        )}
      </div>
    </SidePanelBody>
  );
}

// Member role section
function MemberRoleSection({
  title,
  members,
  onMemberClick,
}: {
  title: string;
  members: Array<{ member: { user_id: string; role?: string }; profile: UserProfile }>;
  onMemberClick: (userId: string) => void;
}) {
  return (
    <section>
      <h3 className="text-body-2-medium text-text-secondary mb-2">{title}</h3>
      <div className="bg-surface rounded-2xl overflow-hidden">
        {members.map(({ member, profile }, index) => (
          <div key={profile.id}>
            {index > 0 && <div className="h-px bg-border-subtle ml-14" />}
            <button
              onClick={() => onMemberClick(profile.id)}
              className="w-full flex items-center gap-3 p-3 hover:bg-surface-1 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border border-border-tertiary">
                {profile.avatar_medium || profile.avatar_small ? (
                  <img
                    src={profile.avatar_medium || profile.avatar_small || ""}
                    alt={userDisplayName(profile)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-surface-1 flex items-center justify-center">
                    <Icon name="user" size={16} className="text-text-tertiary" />
                  </div>
                )}
              </div>
              <span className="flex-1 text-body-2-medium text-text-primary truncate">
                {userDisplayName(profile)}
              </span>
              <Icon name="angle-right" size={14} className="text-text-tertiary" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// Member detail view (placeholder for now)
function MemberDetailView({
  clubId,
  userId,
  members,
}: {
  clubId: string;
  userId: string;
  members: Array<{ user_id: string; role?: string }>;
}) {
  const member = members.find((m) => m.user_id === userId);
  const memberIds = [userId];
  const { data: profiles = [] } = useFetchUserProfilesByIdsQuery(memberIds);
  const profile = profiles[0];

  if (!profile) {
    return (
      <SidePanelBody>
        <div className="text-center py-12">
          <Skeleton className="w-20 h-20 rounded-full mx-auto mb-4" />
          <Skeleton className="h-6 w-32 mx-auto" />
        </div>
      </SidePanelBody>
    );
  }

  const displayName = userDisplayName(profile);
  const currentRole = member?.role || "member";

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-6 pb-4">
        {/* Profile Header */}
        <div className="text-center py-4">
          <div className="w-20 h-20 rounded-full overflow-hidden border border-border-tertiary mx-auto mb-3">
            {profile.avatar_medium || profile.avatar_small ? (
              <img
                src={profile.avatar_medium || profile.avatar_small || ""}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-surface-1 flex items-center justify-center">
                <Icon name="user" size={32} className="text-text-tertiary" />
              </div>
            )}
          </div>
          <h3 className="text-h5 text-text-primary">{displayName}</h3>
          <p className="text-body-2 text-text-secondary capitalize">{currentRole}</p>
        </div>

        {/* Role Management */}
        <div className="bg-surface rounded-2xl p-4">
          <h4 className="text-body-2-medium text-text-secondary mb-3">Role</h4>
          <div className="space-y-2">
            <RoleOption
              label="Owner"
              description="Full control over club settings and members"
              selected={currentRole === "owner"}
              disabled={currentRole === "owner"}
            />
            <RoleOption
              label="Co-host"
              description="Can manage members and create events"
              selected={currentRole === "cohost"}
              disabled={currentRole === "owner"}
            />
            <RoleOption
              label="Member"
              description="Regular club member"
              selected={currentRole === "member"}
              disabled={currentRole === "owner"}
            />
          </div>
          <p className="text-body-3 text-text-tertiary mt-3">
            Role changes coming soon
          </p>
        </div>

        {/* Remove Member */}
        {currentRole !== "owner" && (
          <Button
            variant="outline"
            className="w-full border-border-tertiary text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
            disabled
          >
            Remove from club
          </Button>
        )}
      </div>
    </SidePanelBody>
  );
}

// Role option component
function RoleOption({
  label,
  description,
  selected,
  disabled,
}: {
  label: string;
  description: string;
  selected: boolean;
  disabled: boolean;
}) {
  return (
    <button
      disabled={disabled}
      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-colors ${
        selected ? "bg-surface-1" : "hover:bg-surface-1"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
          selected ? "border-text-primary bg-text-primary" : "border-border-tertiary"
        }`}
      >
        {selected && <Icon name="check" size={10} className="text-background" />}
      </div>
      <div className="flex-1">
        <p className="text-body-2-medium text-text-primary">{label}</p>
        <p className="text-body-3 text-text-tertiary">{description}</p>
      </div>
    </button>
  );
}
